import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import {getAllEventsFromDatabase} from "./BrowserDatabaseFunctions";

const PointsContext = createContext(null);

export const LOYALTY_BASE_PRICE = 7.99;
export const FLOOR_PRICE = 1.99;

export const TIERS = [
    { label: "Explorer",     min: 0,    max: 499,      discount: 0.00 },
    { label: "Navigator",    min: 500,  max: 899,      discount: 1.50 },
    { label: "Trail Seeker",  min: 900,  max: 1499,     discount: 3.00 },
    { label: "Globetrotter",  min: 1500, max: 2499,     discount: 4.50 },
    { label: "Trail Blazer",  min: 2500, max: Infinity, discount: 6.00 },
];

export function PointsProvider({ children, Account }) {
    const [yearlyPoints, setYearlyPoints] = useState(0);
    const [allEvents, setAllEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(false);

    const eventPointsRules = useMemo(() => [
        {
            key: 'meals',
            label: 'Planned Meals',
            points: 20,
            icon: <i className="icon-fork-knife2"/>,
            description: 'Ensure everyone is well-fed! Earn points by adding meal plans to your event.',
            example: 'Add "Dinner at Italian Restaurant" or "Breakfast Buffet" to your schedule.',
        },
        {
            key: 'lodging',
            label: 'Arranged Lodging',
            points: 20,
            icon: <i className="icon-bed"/>,
            description: 'Help your guests find a place to stay. Points for adding accommodation details.',
            example: 'Add hotel bookings or shared Airbnb/VRBO details.',
        },
        {
            key: 'activities',
            label: 'Scheduled Activities',
            points: 20,
            icon: <i className="icon-list-heart"/>,
            description: 'Make your event fun and engaging! Earn points for every activity you plan.',
            example: 'Add "Morning Hike" or "Evening Gala" to your itinerary.',
        },
        {
            key: 'flights',
            label: 'Included Flights',
            points: 20,
            icon: <i className="icon-airplane"/>,
            description: 'If travel is involved, add flight details to help attendees with their journey.',
            example: 'Add flight numbers and times for attendees.',
        },
        {
            key: 'to_do',
            label: 'Added First To-Do Item',
            points: 25,
            icon: <i className="icon-checklist-time" />,
            description: 'Kickstart your event planning by adding your very first task to the To-Do list!',
            example: 'Add "Book catering" to your To-Do list.',
        },
        {
            key: 'ideas',
            label: 'Shared Ideas',
            points: 5,
            icon: <i className="icon-lamp-bright"/>,
            description: 'Encourage collaboration! Earn points for adding suggestions for activities, places to see, or accommodations, and for commenting on others’ ideas.',
            example: 'Add "Hiking at Sunset Trail" or "Try the local bakery" and comment on a friend’s suggestion.',
        },
        {
            key: 'budget',
            label: 'Set Event Budget',
            points: 10,
            icon: <i className="icon-wallet"/>,
            description: 'Keep track of your spending. Points for setting a budget.',
            example: 'Define a budget for your event.',
        },
        {
            key: 'receipts',
            label: 'Uploaded Receipts',
            points: 10,
            icon: <i className="icon-receipt" />,
            description: 'Keep your event organized! Earn points for uploading receipts and tracking expenses.',
            example: 'Upload "Dinner Receipt at Italian Restaurant" or "Hotel Invoice" to your event records.',
        },
        {
            key: 'attendees',
            label: 'Created Guest List',
            points: 15,
            icon: <i className="icon-group"/>,
            description: 'Start inviting! Earn points for creating your guest list.',
            example: 'Add your first guest to the list.',
        },
        {
            key: 'rsvp',
            label: 'Sent Invitations',
            points: 25,
            icon: <i className="icon-envelope-plus"/>,
            description: 'Get the word out! Earn points for sending out invitations.',
            example: 'Send out your first batch of invitations.',
        },
    ], []);

    const accountPointsRules = useMemo(() => [
        { key: 'address', label: 'Add home address', points: 100, icon: <i className="icon-home" />, notes: 'Useful for logistics, time zone, event planning' },
        { key: 'phone', label: 'Add phone number', points: 75, icon: <i className="icon-telephone" />, notes: 'Enhances communication options' },
        { key: 'dob', label: 'Add date of birth', points: 75, icon: <i className="icon-cake" />, notes: 'Demographic targeting and personalization' },
        { key: 'language', label: 'Select preferred language', points: 50, icon: <i className="icon-network" />, notes: 'Improves localization' },
        { key: 'preferred_airport', label: 'Select preferred airport', points: 50, icon: <i className="icon-airplane" />, notes: 'Supports travel planning' },
        { key: 'auto_login', label: 'Enable auto login', points: 25, icon: <i className="icon-key2" />, notes: 'UX preference, low effort' },
        { key: 'notifications', label: 'Configure notification settings', points: 50, icon: <i className="icon-bell-ringing2" />, notes: 'Helps drive timely engagement' },
        { key: 'essentials', label: 'Add first supply essential', points: 25, additionalPointPerEssential: 2, icon: <i className="icon-bag" />, notes: 'Triggers when first "essential" is added' },
        { key: 'documents', label: 'Upload documents (passport, ID, etc.)', isDocument: true, points: 100, icon: <i className="icon-file-lock" />, notes: 'Higher points for effort + real-world value' }
    ], []);

    // LIVE EVALUATION ENGINE FOR INDIVIDUAL EVENTS (2-Argument Matcher)
    const calculateSingleEventPoints = useCallback((event, userId) => {
        if (!event) return { totalEventPoints: 0, eventBreakdown: [] };

        const _isHost = Array.isArray(event.attendees) &&
            event.attendees.some(att => String(att.id) === String(userId) && att.role === 'admin');

        let totalEventPoints = 0;
        const eventBreakdown = [];

        eventPointsRules.forEach(rule => {
            let isFulfilled = false;

            switch (rule.key) {
                case 'meals':
                    isFulfilled = Array.isArray(event.meals) &&
                        event.meals.some(meal => Array.isArray(meal.attendees) && meal.attendees.some(att => String(att.id) === String(userId)));
                    break;
                case 'lodging':
                    isFulfilled = Array.isArray(event.lodging) &&
                        event.lodging.some(l => Array.isArray(l.attendees) && l.attendees.some(att => String(att.id) === String(userId)));
                    break;
                case 'activities':
                    isFulfilled = Array.isArray(event.activities) &&
                        event.activities.some(a => Array.isArray(a.attendees) && a.attendees.some(att => String(att.id) === String(userId)));
                    break;
                case 'flights':
                    isFulfilled = Array.isArray(event.flights) &&
                        event.flights.some(f => f.attendee && String(f.attendee.id) === String(userId));
                    break;
                case 'to_do':
                    isFulfilled = Array.isArray(event.to_do) && event.to_do.some(task => String(task.assigned_to) === String(userId));
                    break;
                case 'ideas':
                    isFulfilled = Array.isArray(event.ideas) && event.ideas.some(idea => String(idea.added_by) === String(userId));
                    break;
                case 'receipts':
                    isFulfilled = Array.isArray(event.receipts) && event.receipts.some(r => String(r.attendee_id) === String(userId));
                    break;
                case 'budget':
                    isFulfilled = Array.isArray(event.attendees) &&
                        event.attendees.some(att => {
                            const budget = att.budget?.total_budget;
                            return String(att.id) === String(userId) && budget != null && budget !== "" && Number(budget) > 0;
                        });
                    break;
                case 'attendees':
                    if (_isHost) isFulfilled = Array.isArray(event.attendees) && event.attendees.some(att => att.role === 'guest');
                    break;
                case 'rsvp':
                    if (_isHost) isFulfilled = Array.isArray(event.attendees) && event.attendees.some(att => att.rsvp_sent === true);
                    break;
                default:
                    break;
            }

            if (isFulfilled) totalEventPoints += rule.points;

            eventBreakdown.push({
                key: rule.key,
                label: rule.label,
                points: rule.points,
                isFulfilled,
                icon: rule.icon,
                description: rule.description,
                example: rule.example,
            });
        });

        return { totalEventPoints, eventBreakdown };
    }, [eventPointsRules]);

    // CALCULATE ACCOUNT DETAILS AND POINTS LIVE
    const { allPointsDetails, totalAccountPoints } = useMemo(() => {
        const details = [];
        let currentTotalPoints = 0;

        accountPointsRules.forEach(rule => {
            let isFulfilled = false;
            let earnedPoints = 0;
            let currentLabel = rule.label;
            let currentNotes = '';
            let statusMessage = 'Incomplete';

            if (!Account) {
                currentNotes = `One time ${rule.points} pts`;
            } else if (rule.isDocument) {
                const docs = Account.documents || [];
                const docCount = docs.length || 0;
                if (docCount > 0) {
                    isFulfilled = true;
                    earnedPoints = docCount * rule.points;
                    currentLabel = `${rule.label} (${docCount})`;
                    statusMessage = `Completed (${docCount} items)`;
                }
                currentNotes = `100 pts for each Document and you have ${docCount}`;
            } else if (rule.key === 'essentials') {
                const essentials = Account.essentials || [];
                const essentialCount = essentials.length || 0;
                if (essentialCount > 0) {
                    isFulfilled = true;
                    earnedPoints = rule.points + (essentials.length > 1 ? ((essentials.length - 1) * 2) : 0);
                    statusMessage = 'Completed';
                }
                currentNotes = `First item ${rule.points} pts; each additional item is ${rule.additionalPointPerEssential || 0} pts each`;
            } else if (Account[rule.key]) {
                isFulfilled = true;
                earnedPoints = rule.points;
                statusMessage = 'Completed';
                currentNotes = `One time ${rule.points} pts`;
            } else {
                currentNotes = `One time ${rule.points} pts`;
            }

            currentTotalPoints += earnedPoints;

            details.push({
                key: rule.key,
                label: currentLabel,
                potentialPoints: rule.points,
                earned: earnedPoints,
                isFulfilled: isFulfilled,
                icon: rule.icon,
                notes: currentNotes,
                statusMessage: statusMessage
            });
        });

        return { allPointsDetails: details, totalAccountPoints: currentTotalPoints };
    }, [Account, accountPointsRules]);

    // FETCH PAST CALENDAR ENTRIES FROM DB ON USER HANDSHAKE
    useEffect(() => {
        if (!Account?.id) {
            setYearlyPoints(0);
            setAllEvents([]);
            return;
        }

        async function fetchHistory() {
            setLoadingEvents(true);
            try {
                const fetchedEvents = await getAllEventsFromDatabase(Account.id);
                if (!fetchedEvents) {
                    setAllEvents([]);
                    return;
                }
                setAllEvents(fetchedEvents);

                const currentYear = dayjs().year();
                const eventsThisYear = fetchedEvents.filter(event => {
                    const dates = event.time_table?.dates || [];
                    return dates.length && dayjs(dates[0].date).year() === currentYear;
                });

                const total = eventsThisYear.reduce((acc, ev) => {
                    const { totalEventPoints } = calculateSingleEventPoints(ev, Account.id);
                    return acc + (totalEventPoints || 0);
                }, 0);

                setYearlyPoints(total);
            } catch (err) {
                console.error("Error evaluating points history context", err);
            } finally {
                setLoadingEvents(false);
            }
        }
        fetchHistory();
    }, [Account?.id, calculateSingleEventPoints]);

    const getCurrentTier = useCallback((points) => {
        return TIERS.find(tier => points >= tier.min && points <= tier.max) || TIERS[0];
    }, []);

    const getNextTier = useCallback((points) => {
        const current = getCurrentTier(points);
        if (!current) return null;
        return TIERS.find(tier => tier.min > current.min) || null;
    }, [getCurrentTier]);

    const value = useMemo(() => ({
        totalAccountPoints,
        yearlyPoints,
        allPointsDetails,
        allEvents,
        calculateSingleEventPoints,
        loadingEvents,
        eventPointsRules,
        accountPointsRules,
        TIERS,
        LOYALTY_BASE_PRICE,
        FLOOR_PRICE,
        getCurrentTier,
        getNextTier
    }), [totalAccountPoints, yearlyPoints, allPointsDetails, allEvents, calculateSingleEventPoints, loadingEvents, eventPointsRules, accountPointsRules, getCurrentTier, getNextTier]);

    return (
        <PointsContext.Provider value={value}>
            {children}
        </PointsContext.Provider>
    );
}

export function usePoints() {
    const context = useContext(PointsContext);
    if (!context) throw new Error("usePoints must be wrapped inside a PointsProvider");
    return context;
}